import { CssBaseline } from '@material-ui/core';
import * as locales from '@material-ui/core/locale';
import { createMuiTheme } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import { AlertClassKey, AlertTitleClassKey } from '@material-ui/lab';
import { MuiPickersOverrides } from '@material-ui/pickers/typings/overrides';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/styles';
import React, { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';

import { CircularTimerClassKey } from '@/components/output/CircularTimer';
import { container } from '@/utils/ui';

const tryLocale = (locale: string): [string, Record<string, string>] | undefined => {
  const foundLocale = ((locales as unknown) as Record<string, Record<string, string>>)[locale.replace('-', '')];
  return foundLocale && [locale, foundLocale];
};

const parseLocale = (locale: string): [string, Record<string, string>] =>
  tryLocale(locale) as [string, Record<string, string>];

interface ThemeColors {
  primary: string;
  primaryBackground: string;
  secondaryContrast: string;
  secondary: string;
  lightContrast: string;
  light: string;
  lightBackground: string;
  accent: string;
  error: string;
}

declare module '@material-ui/core/styles/overrides' {
  export interface ComponentNameToClassKey extends MuiPickersOverrides {
    MuiAlert: AlertClassKey;
    MuiAlertTitle: AlertTitleClassKey;
    CircularTimer: CircularTimerClassKey;
  }
}

declare module '@material-ui/core/styles/createMuiTheme' {
  interface Theme {
    MainContainer: {
      height: number;
      width: number;
    };
    colors: ThemeColors;
  }

  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    MainContainer?: {
      height: number;
      width: number;
    };
    colors: ThemeColors;
  }
}

const crateTheme = (locale: string): Theme => {
  const colors: ThemeColors = {
    primary: '#141233',
    primaryBackground: '#FFFFFF',
    secondaryContrast: '#999BBF',
    secondary: '#C3C3D9',
    lightContrast: '#E9E9F2',
    lightBackground: '#F9F9FC',
    light: '#F2F2F7',
    accent: '#00C489',
    error: '#F22462',
  };

  return createMuiTheme(
    {
      colors,
      MainContainer: {
        height: container.defaultDesktopHeight,
        width: container.mobileSwitchWidth,
      },
      overrides: {
        CircularTimer: {
          colorPrimary: {
            color: colors.primary,
          },
          colorSecondary: {
            color: colors.lightContrast,
          },
        },
        MuiLinearProgress: {
          colorPrimary: {
            backgroundColor: colors.primary,
          },
          barColorPrimary: {
            backgroundColor: colors.lightContrast,
          },
        },
        MuiAlert: {
          root: {
            borderRadius: '12px',
          },
          filledSuccess: {
            backgroundColor: colors.accent,
          },
          filledError: {
            backgroundColor: colors.error,
          },
          message: {
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '18px',
          },
        },
        MuiAlertTitle: {
          root: {
            color: 'white',
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '24px',
          },
        },
        MuiDivider: {
          light: {
            backgroundColor: colors.secondary,
          },
        },
        MuiFormHelperText: {
          root: {
            color: colors.secondary,
            marginLeft: 0,
            marginRight: 0,
            fontSize: '13px',
            lineHeight: '17px',
            '&$error': {
              margin: 0,
              color: colors.error,
              fontSize: '13px',
              lineHeight: '17px',
            },
          },
          contained: {
            marginLeft: 0,
            marginRight: 0,
          },
        },
        MuiFormControl: {
          root: {
            flexShrink: 0,
          },
        },
        MuiInputLabel: {
          filled: {
            color: colors.secondaryContrast,
            '&$error': {
              color: colors.error,
            },
            '&$focused': {
              color: colors.secondaryContrast,
            },
          },
        },
        MuiFormLabel: {
          filled: {
            color: colors.secondaryContrast,
            '&$error': {
              color: colors.error,
            },
            '&$focused': {
              color: colors.secondaryContrast,
            },
          },
        },
        MuiInput: {
          root: {
            backgroundColor: colors.light,
            borderRadius: '12px',
            '&$error': {
              '& > input': {
                color: colors.error,
              },
              '& > .MuiInputAdornment-root > button': {
                color: colors.error,
              },
            },
            '& > .MuiInputAdornment-root > button': {
              color: colors.error,
            },
          },
          input: {
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingBottom: '10px',
            paddingTop: '24px',
            color: colors.primary,
            lineHeight: '22px',
            height: '22px',
            fontSize: '16px',
            '&::placeholder': {
              color: colors.secondaryContrast,
            },
          },
        },
        MuiFilledInput: {
          root: {
            backgroundColor: colors.light,
            borderRadius: '12px',
            borderTopRightRadius: '12px',
            borderTopLeftRadius: '12px',
            '&$focused': {
              backgroundColor: colors.light,
            },
            '&$error': {
              '& > input': {
                color: colors.error,
              },
              '& > .MuiInputAdornment-root > button': {
                color: colors.error,
              },
            },
            '& > .MuiInputAdornment-root > button': {
              color: colors.primary,
            },
            '&:hover': {
              '@media (hover: none)': {
                backgroundColor: colors.light,
              },
              backgroundColor: colors.light,
            },
          },
          input: {
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingBottom: '10px',
            paddingTop: '24px',
            color: colors.primary,
            lineHeight: '22px',
            height: '22px',
            fontSize: '16px',
            '&::placeholder': {
              color: colors.secondaryContrast,
            },
          },
        },
        MuiCheckbox: {
          root: {
            color: colors.lightContrast,
            '&$checked': {
              color: colors.accent,
            },
            '& .MuiIconButton-label': {
              position: 'relative',
              zIndex: 0,
            },
            '&:not($checked) .MuiIconButton-label:after': {
              content: '""',
              left: 4,
              top: 4,
              height: 15,
              width: 15,
              position: 'absolute',
              backgroundColor: colors.lightContrast,
              zIndex: -1,
            },
          },
          colorSecondary: {
            '&$checked': {
              color: colors.accent,
            },
          },
          colorPrimary: {
            '&$checked': {
              color: colors.accent,
            },
          },
          checked: {},
        },
        MuiButton: {
          root: {
            '&$disabled': {
              '@media (hover: none)': {
                color: colors.secondaryContrast,
                backgroundColor: colors.lightContrast,
                borderColor: colors.lightContrast,
              },
              '&:hover': {
                '&$disabled': {
                  color: colors.secondaryContrast,
                  backgroundColor: colors.lightContrast,
                  borderColor: colors.lightContrast,
                },
              },
              color: colors.secondaryContrast,
              backgroundColor: colors.lightContrast,
              borderColor: colors.lightContrast,
            },
          },
          outlinedPrimary: {
            backgroundColor: colors.accent,
            borderColor: colors.accent,
            borderRadius: '12px',
            padding: 0,
            fontWeight: 500,
            fontSize: '17px',
            lineHeight: '56px',
            color: colors.primaryBackground,
            '&:hover': {
              '@media (hover: none)': {
                color: colors.primaryBackground,
                backgroundColor: colors.accent,
                borderColor: colors.accent,
              },
              color: colors.primaryBackground,
              backgroundColor: colors.accent,
              borderColor: colors.accent,
            },
          },
          outlinedSecondary: {
            backgroundColor: colors.lightContrast,
            borderColor: colors.lightContrast,
            borderRadius: '12px',
            padding: 0,
            fontSize: '17px',
            lineHeight: '56px',
            color: colors.primary,
            '&:hover': {
              '@media (hover: none)': {
                color: colors.primary,
                backgroundColor: colors.lightContrast,
                borderColor: colors.lightContrast,
              },
              color: colors.primary,
              backgroundColor: colors.lightContrast,
              borderColor: colors.lightContrast,
            },
          },
          outlined: {
            '&$disabled': {
              borderColor: colors.lightContrast,
            },
          },
        },
        MuiLink: {
          root: {
            color: colors.accent,
          },
        },
        MuiPickersToolbar: {
          toolbar: {
            backgroundColor: colors.primary,
          },
        },
        MuiPickersDay: {
          daySelected: {
            color: '#FFFFFF',
          },
        },
      },
      typography: {
        fontFamily: 'Roboto',
        h1: {
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '20px',
          lineHeight: '26px',
          textAlign: 'left',
        },
        h2: {
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '18px',
          lineHeight: '24px',
          textAlign: 'left',
        },
        h3: {
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '15px',
          lineHeight: '22px',
          textAlign: 'left',
        },
        h4: {
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
        },
        body1: {
          fontStyle: 'normal',
          fontWeight: 'normal',
          fontSize: '14px',
          lineHeight: '20px',
        },
        body2: {
          fontStyle: 'normal',
          fontWeight: 'normal',
          fontSize: '14px',
          lineHeight: '20px',
        },
        button: {
          width: '100%',
          fontStyle: 'normal',
          fontWeight: 'normal',
          textTransform: 'inherit',
        },
        overline: {
          color: colors.accent,
        },
      },
    },
    parseLocale(locale),
  );
};

const withTheme = (Component: React.FunctionComponent) => ({
  locale = window.env.DEFAULT_LOCALE,
  ...props
}: {
  locale?: string;
  [_: string]: unknown;
}): JSX.Element => {
  const theme = useMemo(() => crateTheme(locale), [locale]);
  return (
    // MuiThemeProvider makes the theme available down the React tree
    // thanks to React context.
    <MuiThemeProvider theme={theme}>
      <ThemeProvider theme={theme}>
        {/* Reboot kickstart an elegant, consistent, and simple baseline to build upon. */}
        <CssBaseline />
        <Component {...props} />
      </ThemeProvider>
    </MuiThemeProvider>
  );
};

export default withTheme;
